import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import {
  ChevronDown,
  Filter,
  Inbox,
  LayoutDashboard,
  LogOut,
  Mail,
  Menu,
  Settings,
  ShieldCheck,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { useAuth } from '@/store/auth';
import { useLiveEvents, type LiveStatus } from '@/lib/useLiveEvents';
import { Badge, useDismissOnOutsideClick } from '@/components/ui';
import { Logo } from '@/components/Logo';

type NavItem = { to: string; label: string; icon: typeof Inbox; end?: boolean };

/**
 * Navigation is per-role and deliberately short. Each role sees only the
 * screens it can act on — an admin has no mailboxes of their own, and an end
 * user has no reason to see platform-wide data.
 */
const NAV_BY_ROLE: Record<string, { title: string; items: NavItem[] }> = {
  USER: {
    title: 'Monitoring',
    items: [
      { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
      { to: '/mailboxes', label: 'Mailboxes', icon: Inbox },
      { to: '/keywords', label: 'Keywords', icon: Filter },
      { to: '/settings', label: 'Settings', icon: Settings },
    ],
  },
  ADMIN: {
    title: 'Administration',
    items: [
      { to: '/admin/add-user', label: 'Add user', icon: UserPlus },
      { to: '/admin/users', label: 'Users', icon: Users },
      { to: '/settings', label: 'Settings', icon: Settings },
    ],
  },
  SUPER_ADMIN: {
    title: 'Platform',
    items: [
      { to: '/super/users', label: 'Users', icon: Users },
      { to: '/super/admins', label: 'Administrators', icon: ShieldCheck },
      { to: '/super/mails', label: 'Mail & brands', icon: Mail },
      { to: '/settings', label: 'Settings', icon: Settings },
    ],
  },
};

function LiveIndicator({ status }: { status: LiveStatus }) {
  const config = {
    live: { tone: 'success' as const, label: 'Live', dot: 'bg-emerald-400' },
    connecting: { tone: 'info' as const, label: 'Connecting', dot: 'bg-sky-400' },
    offline: { tone: 'neutral' as const, label: 'Reconnecting', dot: 'bg-slate-500' },
  }[status];

  return (
    <Badge tone={config.tone}>
      <span className={clsx('h-1.5 w-1.5 rounded-full', config.dot, status === 'live' && 'animate-pulse')} />
      {config.label}
    </Badge>
  );
}

function NavSection({
  title,
  items,
  onNavigate,
}: {
  title: string;
  items: NavItem[];
  onNavigate: () => void;
}) {
  return (
    <div className="mb-6">
      <p className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-600">{title}</p>
      <nav className="space-y-0.5">
        {items.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={onNavigate}
            className={({ isActive }) =>
              clsx(
                'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition',
                isActive
                  ? 'bg-brand-500/10 text-brand-300 ring-1 ring-inset ring-brand-500/20'
                  : 'text-slate-400 hover:bg-surface-hover hover:text-slate-200',
              )
            }
          >
            <Icon className="h-4 w-4 shrink-0" aria-hidden />
            {label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

export function AppShell() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const liveStatus = useLiveEvents(user?.role === 'USER');
  const menuRef = useDismissOnOutsideClick<HTMLDivElement>(menuOpen, () => setMenuOpen(false));

  if (!user) return null;

  const nav = NAV_BY_ROLE[user.role] ?? NAV_BY_ROLE.USER!;

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  const sidebar = (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center px-5">
        <Logo />
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-4">
        <NavSection title={nav.title} items={nav.items} onNavigate={() => setMobileOpen(false)} />
        {/* A super admin can also use the admin screens to provision users. */}
        {user.role === 'SUPER_ADMIN' ? (
          <NavSection
            title="Provisioning"
            items={NAV_BY_ROLE.ADMIN!.items.filter((i) => i.to !== '/settings')}
            onNavigate={() => setMobileOpen(false)}
          />
        ) : null}
      </div>


    </div>
  );

  return (
    <div className="flex h-full">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 border-r border-surface-border bg-surface-raised/50 lg:block">
        {sidebar}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen ? (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 animate-slide-up border-r border-surface-border bg-surface-raised">
            {sidebar}
          </aside>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center justify-between gap-4 border-b border-surface-border bg-surface-base/85 px-4 backdrop-blur-md sm:px-6">
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="rounded-lg p-2 text-slate-400 transition hover:bg-surface-hover hover:text-slate-100 lg:hidden"
            aria-label="Toggle navigation"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>

          <div className="flex-1" />

          <div className="flex items-center gap-3">
            <LiveIndicator status={liveStatus} />

            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen((v) => !v)}
                className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-slate-300 transition hover:bg-surface-hover"
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-500/20 text-xs font-semibold text-brand-300">
                  {user.fullName.slice(0, 1).toUpperCase()}
                </span>
                <span className="hidden max-w-[10rem] truncate sm:inline">{user.fullName}</span>
                <ChevronDown className="h-3.5 w-3.5 text-slate-500" aria-hidden />
              </button>

              {menuOpen ? (
                <div className="absolute right-0 mt-2 w-56 animate-fade-in overflow-hidden rounded-xl border border-surface-border bg-surface-raised shadow-2xl">
                  <div className="border-b border-surface-border px-4 py-3">
                    <p className="truncate text-sm font-medium text-slate-200">{user.fullName}</p>
                    <p className="mt-0.5 truncate text-xs text-slate-500">{user.email}</p>
                  </div>
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      navigate('/settings');
                    }}
                    className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-slate-300 transition hover:bg-surface-hover"
                  >
                    <Settings className="h-4 w-4" aria-hidden />
                    Account settings
                  </button>
                  <button
                    onClick={handleSignOut}
                    className="flex w-full items-center gap-2.5 border-t border-surface-border px-4 py-2.5 text-sm text-red-400 transition hover:bg-red-500/10"
                  >
                    <LogOut className="h-4 w-4" aria-hidden />
                    Sign out
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-[1600px] px-4 py-6 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
